// MEPFLSInfrastructureTable.tsx
import React from 'react';

export interface MEPFLSInfrastructureRow {
  srNo: number;
  systems: string;
  totalInstalledNos: number | null;
  workingNos: number | null;
  process: string;
  status: string | null;
  completionDateOrRemark: string | null;
}

interface MEPFLSInfrastructureTableProps {
  data?: MEPFLSInfrastructureRow[];
  onChange?: (index: number, updatedRow: MEPFLSInfrastructureRow) => void;
  isEditable?: boolean;
}

const statusOptions = ['YES', 'NO', 'N/A'];

// Sr.Nos linked to KPI Fire Safety Scorecard parameters (must match syncMISToKPI.ts)
const KPI_LINKED_SR_NOS: number[] = [41, 44, 50, 56, 59];

export default function MEPFLSInfrastructureTable({
  data = [],
  onChange,
  isEditable = true,
}: MEPFLSInfrastructureTableProps) {
  const handleFieldChange = (
    idx: number,
    field: keyof MEPFLSInfrastructureRow,
    value: string | number | null
  ) => {
    if (!onChange || !isEditable) return;

    const row = data[idx];
    let processedValue: string | number | null = value;

    // Handle numeric fields
    if (field === 'totalInstalledNos' || field === 'workingNos') {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'na' || value.toLowerCase() === 'n/a' || value === '') {
          processedValue = null;
        } else {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
      }
    }

    // Validate and auto-adjust workingNos if it exceeds totalInstalledNos
    if (field === 'workingNos' && processedValue !== null && row.totalInstalledNos !== null) {
      if (typeof processedValue === 'number' && processedValue > row.totalInstalledNos) {
        alert(`Inspected & Working Healthy & compliance (In Nos.) cannot exceed Installed (In Nos.). Value has been adjusted to match Installed (${row.totalInstalledNos}).`);
        processedValue = row.totalInstalledNos; // Auto-adjust to installed value
      }
    }

    // Prevent editing of non-editable fields
    if (field === 'srNo' || field === 'systems' || field === 'process') {
      return; // Prevent editing of srNo, systems, and process
    }

    onChange(idx, {
      ...row,
      [field]: processedValue,
    });
  };

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="bg-green-600 text-white px-4 py-2 font-bold text-lg mb-0 rounded-t-lg">
        (II) Fire and Life safety system
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-20">
              Sr.No
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[250px]">
              Systems
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Installed (In Nos.)
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-32">
              Inspected & Working Healthy & compliance (In Nos.)
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[400px]">
              Process
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 min-w-[120px] w-32">
              Status
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 min-w-[250px]">
              Completion date / Remark
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isLinked = KPI_LINKED_SR_NOS.includes(row.srNo);
            return (
              <tr
                key={idx}
                className={`transition-colors ${isLinked
                  ? 'bg-teal-50 hover:bg-teal-100 border-l-4 border-l-teal-500'
                  : 'bg-white hover:bg-gray-50'
                  }`}
                title={isLinked ? 'Linked to KPI Fire Safety Scorecard' : undefined}
              >
                <td className={`border border-gray-300 px-3 py-2 text-center text-sm font-medium ${isLinked ? 'text-teal-800' : 'text-gray-700'
                  }`}>
                  <span>{row.srNo || ''}</span>
                </td>
                <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                  <span className="whitespace-pre-wrap break-words">{row.systems || ''}</span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.totalInstalledNos)}
                      onChange={(e) => handleFieldChange(idx, 'totalInstalledNos', e.target.value)}
                      placeholder="N/A"
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.totalInstalledNos !== null && row.totalInstalledNos !== undefined ? row.totalInstalledNos : 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.workingNos)}
                      onChange={(e) => handleFieldChange(idx, 'workingNos', e.target.value)}
                      placeholder="N/A"
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 font-medium">
                      {row.workingNos !== null && row.workingNos !== undefined ? row.workingNos : 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-4 py-2 text-sm text-gray-700">
                  <span className="whitespace-pre-wrap break-words">{row.process || ''}</span>
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center min-w-[120px]">
                  {isEditable ? (
                    <select
                      value={row.status || ''}
                      onChange={(e) => handleFieldChange(idx, 'status', e.target.value || null)}
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-w-[100px]"
                    >
                      <option value="">Select Status</option>
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-700 font-medium inline-block px-2 py-1 bg-gray-100 rounded">
                      {row.status || 'N/A'}
                    </span>
                  )}
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  {(() => {
                    const statusLower = (row.status || '').toLowerCase();
                    const requiresRemarks = statusLower === 'no' || statusLower === 'partially';
                    const isMissing = requiresRemarks && !row.completionDateOrRemark?.trim();
                    return isEditable ? (
                      <div>
                        <textarea
                          value={row.completionDateOrRemark || ''}
                          onChange={(e) => handleFieldChange(idx, 'completionDateOrRemark', e.target.value || null)}
                          placeholder={requiresRemarks ? 'Remarks required *' : 'Enter completion date or remark'}
                          rows={2}
                          className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:border-transparent resize-y ${isMissing
                            ? 'border-red-400 bg-red-50 focus:ring-red-500 placeholder-red-400'
                            : 'border-gray-400 bg-white focus:ring-blue-500'
                            }`}
                        />
                        {isMissing && (
                          <p className="text-xs text-red-500 mt-1">Remarks required for "{row.status}" status</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">{row.completionDateOrRemark || 'N/A'}</span>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

